// Auth TypeScript (Login & Reset)

// ============================================================================
// Import Zod Schemas for Runtime Validation
// ============================================================================

import {
  LoginResponseSchema,
  PasskeyAuthenticationOptionsSchema,
  PasskeyRegistrationOptionsSchema,
  PasskeyListResponseSchema,
  SimpleSuccessResponseSchema
} from './types/schemas.js';

// ============================================================================
// Type Definitions (using global types from global.d.ts)
// ============================================================================

interface PasskeyRegisterResponse {
  success: boolean;
  message?: string;
  error?: string;
  publicKey?: PublicKeyCredentialCreationOptionsJSON;
  challenge_id?: string;
  rp_id?: string;
}

interface PasskeyAuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  publicKey?: PublicKeyCredentialRequestOptionsJSON;
  challenge_id?: string;
  rp_id?: string;
  redirect?: string;
}

interface PasskeyListItem {
  id: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
}

interface PasskeyListResponse {
  success: boolean;
  items: PasskeyListItem[];
}

interface ApiResponse<T> {
  status: number;
  data: T;
}

interface PendingUser {
  id: string;
  email: string;
  account_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface UserListItem {
  id: string;
  email: string;
  account_status: 'pending' | 'approved' | 'rejected';
  is_active: number | string;
  is_admin: number | string;
  consent_ok: number | string;
  account_type: 'business' | 'private';
  company_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  uid_number?: string;
  billing_city?: string;
  billing_country?: string;
  created_at: string;
  last_login?: string;
  consent_accepted_at?: string;
  consent_ip?: string;
  consent_remote_addr?: string;
  consent_user_agent?: string;
}

interface AuditLogItem {
  action: string;
  meta_json?: string;
  actor_email?: string;
  target_email?: string;
  created_at: string;
  ip?: string;
  remote_addr?: string;
  forwarded_for?: string;
  user_agent?: string;
}

interface ConsentLogItem {
  email: string;
  accepted_at: string;
  consent_key: string;
  version: string;
  ip?: string;
  remote_addr?: string;
  forwarded_for?: string;
  user_agent?: string;
}

interface ActiveSession {
  email: string;
  account_status: string;
  session_count: string;
  is_active: number | string;
  expires_at: string;
}

interface ReactivationStatus {
  can_reactivate: boolean;
  days_remaining: number;
  has_open_ticket: boolean;
  ticket?: {
    number: string;
    status: string;
  };
}

interface ReactivationStatusResponse {
  success: boolean;
  data?: ReactivationStatus;
}

interface PostFormOptions {
  onStepupRequired?: (data: unknown) => void;
}

// Extended PublicKeyCredential types for WebAuthn operations
type ExtendedPublicKeyCredentialCreationOptions = Omit<PublicKeyCredentialCreationOptionsJSON, 'challenge' | 'user' | 'excludeCredentials'> & {
  challenge: ArrayBuffer;
  user: {
    id: ArrayBuffer;
    name: string;
    displayName: string;
  };
  excludeCredentials?: Array<{
    type: string;
    id: ArrayBuffer;
    transports: string[];
  }>;
};

type ExtendedPublicKeyCredentialRequestOptions = Omit<PublicKeyCredentialRequestOptionsJSON, 'challenge' | 'allowCredentials'> & {
  challenge: ArrayBuffer;
  allowCredentials?: Array<{
    type: string;
    id: ArrayBuffer;
    transports?: string[];
  }>;
};

// ============================================================================
// Main Auth Module
// ============================================================================

(function() {
    'use strict';

    // Reference to shared utilities (loaded via utils.js)
    const utils = window.CybUtils || {};
    const escapeHtml = (utils as { escapeHtml?: (s: string | number | null | undefined) => string }).escapeHtml || function(s: string | number | null | undefined): string {
        // Fallback if utils.js not loaded
        if (s === null || s === undefined) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    function init(): void {
        setupLogin();
        setupPasskeyLogin();
        setupSignup();
        setupEmailChange();
        setupProfile();
        setupMailSettings();
        setupPasskeys();
        setupApprovals();
        setupUserManagement();
        setupSignupSettings();
        setupLogs();
        setupAdminOverview();
    }

    function webauthnSupported(): boolean {
        return !!(window.PublicKeyCredential && navigator.credentials);
    }

    /**
     * Decode base64url or lbuchs ByteBuffer JSON format to ArrayBuffer.
     * Fixed: Added additional sanitization to prevent "string did not match pattern" errors.
     */
    function b64urlToBuf(b64url: string, fieldName?: string): ArrayBuffer {
        const context = fieldName ? ('[' + fieldName + '] ') : '';

        if (!b64url) {
            throw new Error(context + 'b64urlToBuf: Input is empty or null');
        }

        let input = String(b64url);
        const originalPreview = input.substring(0, 20) + (input.length > 20 ? '...' : '');

        // Remove all whitespace and non-base64url characters
        input = input.replace(/\s/g, '');
        
        // Handle MIME encoded-word format (legacy compatibility)
        const binaryMatch = input.match(/^=\?BINARY\?B\?(.+)\?=$/i);
        if (binaryMatch) {
            input = binaryMatch[1];
        }

        if (input.length === 0) {
            throw new Error(context + 'b64urlToBuf: Input is empty after stripping whitespace (original: "' + originalPreview + '")');
        }

        // Convert base64url to standard base64
        let s: string;
        if (input.match(/^[A-Za-z0-9_-]+$/)) {
            // Pure base64url (no padding, - and _ instead of + and /)
            s = input.replace(/-/g, '+').replace(/_/g, '/');
        } else if (input.match(/^[A-Za-z0-9+/]+={0,2}$/)) {
            // Already standard base64
            s = input;
        } else {
            // Mixed or unknown format - sanitize aggressively
            s = input.replace(/[^A-Za-z0-9_-]/g, '')  // Remove invalid chars
                     .replace(/-/g, '+')
                     .replace(/_/g, '/');
        }

        // Remove existing padding
        s = s.replace(/=+$/, '');
        
        // Add proper padding (must be multiple of 4)
        const pad = s.length % 4;
        if (pad) {
            s += new Array(5 - pad).join('=');
        }

        // Final validation - only allow valid base64 characters
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s)) {
            throw new Error(context + 'b64urlToBuf: Input contains invalid base64 structure. Input preview: "' + originalPreview + '"');
        }

        try {
            const bin = atob(s);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) {
                bytes[i] = bin.charCodeAt(i);
            }
            return bytes.buffer;
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : 'Invalid encoding';
            throw new Error(context + 'b64urlToBuf: Failed to decode base64 data - ' + errorMsg + '. Input preview: "' + originalPreview + '"');
        }
    }

    function bufToB64url(buf: ArrayBuffer): string {
        const bytes = new Uint8Array(buf);
        let bin = '';
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        const b64 = btoa(bin);
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    }

    /**
     * Decode standard base64 to ArrayBuffer (for Safari/WebAuthn compatibility).
     */
    function b64ToBuf(b64: string, fieldName?: string): ArrayBuffer {
        const context = fieldName ? ('[' + fieldName + '] ') : '';
        if (!b64) {
            throw new Error(context + 'b64ToBuf: Input is empty or null');
        }
        let input = String(b64).replace(/\s/g, '');
        const originalPreview = input.substring(0, 20) + (input.length > 20 ? '...' : '');

        const binaryMatch = input.match(/^=\?BINARY\?B\?(.+)\?=$/i);
        if (binaryMatch) {
            input = binaryMatch[1];
        }

        if (input.length === 0) {
            throw new Error(context + 'b64ToBuf: Input is empty after processing (original: "' + originalPreview + '")');
        }

        const pad = input.length % 4;
        if (pad) {
            input += new Array(5 - pad).join('=');
        }

        if (!/^[A-Za-z0-9+/=]+$/.test(input)) {
            throw new Error(context + 'b64ToBuf: Invalid base64 characters. Input preview: "' + originalPreview + '"');
        }

        try {
            const bin = atob(input);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) {
                bytes[i] = bin.charCodeAt(i);
            }
            return bytes.buffer;
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : 'Invalid encoding';
            throw new Error(context + 'b64ToBuf: Failed to decode - ' + errorMsg + '. Input preview: "' + originalPreview + '"');
        }
    }

    function normalizeAllowCredential(c: { type?: string; id?: string; transports?: unknown }, idField: string): { type: string; id: ArrayBuffer } {
        if (!c || !c.id) {
            throw new Error('Ungueltige AllowCredential-Daten: id fehlt (' + idField + ')');
        }
        const normalized: { type: string; id: ArrayBuffer } = {
            type: 'public-key',
            id: b64urlToBuf(c.id, idField)
        };
        return normalized;
    }

    function isPatternError(err: unknown): boolean {
        const msg = err instanceof Error ? err.message : String(err || '');
        return msg.indexOf('The string did not match the expected pattern') !== -1;
    }

    function getCredentialWithFallback(pk: ExtendedPublicKeyCredentialRequestOptions): Promise<Credential | null> {
        return navigator.credentials.get({ publicKey: pk as unknown as PublicKeyCredentialRequestOptions }).catch(function(err: unknown) {
            if (isPatternError(err) && pk.allowCredentials && pk.allowCredentials.length > 0) {
                const fallbackPk = Object.assign({}, pk);
                delete (fallbackPk as { allowCredentials?: unknown }).allowCredentials;
                return navigator.credentials.get({ publicKey: fallbackPk as unknown as PublicKeyCredentialRequestOptions });
            }
            throw err;
        });
    }

    function getCsrfFromForm(form: HTMLFormElement | null): string {
        const el = form?.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
        const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
        return String(el?.value || meta?.content || '');
    }

    function setCsrfOnForm(form: HTMLFormElement | null, csrfToken: string): void {
        if (!form) return;
        const v = String(csrfToken || '');
        if (!v) return;
        let el = form.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
        if (!el) {
            el = document.createElement('input');
            el.type = 'hidden';
            el.name = 'csrf_token';
            form.appendChild(el);
        }
        el.value = v;
    }

    function doPasskeyStepup(csrfToken: string): Promise<boolean> {
        if (!webauthnSupported()) {
            return Promise.reject(new Error('WebAuthn nicht verfuegbar'));
        }

        return window.api.post('/passkey-stepup-options', { csrf_token: csrfToken || '' })
        .then(function(data: unknown) {
            const r: ApiResponse<PasskeyAuthResponse> = { status: 200, data: data as PasskeyAuthResponse };
            if (!r || !r.data || !r.data.publicKey || !r.data.challenge_id) {
                if (r && r.data && r.data.error === 'webauthn_ip_origin_not_allowed') {
                    throw new Error(r.data.message || 'Passkeys require a domain name, not an IP address. Please use a hostname.');
                }
                throw new Error((r && r.data && (r.data.message || r.data.error)) || 'Step-up fehlgeschlagen');
            }
            const pk = r.data.publicKey as unknown as ExtendedPublicKeyCredentialRequestOptions;
            pk.challenge = b64urlToBuf(r.data.publicKey.challenge, 'stepup.challenge');
            if (r.data.publicKey.allowCredentials && Array.isArray(r.data.publicKey.allowCredentials)) {
                pk.allowCredentials = r.data.publicKey.allowCredentials.map(function(c, idx) {
                    return normalizeAllowCredential(c, 'stepup.allowCredentials[' + idx + '].id');
                });
            }
            return getCredentialWithFallback(pk).then(function(cred) {
                if (!cred || !(cred as PublicKeyCredential).response) throw new Error('Step-up abgebrochen');
                const pkCred = cred as PublicKeyCredential;
                const response = pkCred.response as AuthenticatorAssertionResponse;
                return window.api.post('/passkey-stepup-verify', {
                    csrf_token: csrfToken || '',
                    challenge_id: r.data.challenge_id,
                    id: pkCred.id,
                    clientDataJSON: bufToB64url(response.clientDataJSON),
                    authenticatorData: bufToB64url(response.authenticatorData),
                    signature: bufToB64url(response.signature),
                    userHandle: response.userHandle ? bufToB64url(response.userHandle) : ''
                }).then(function(d2: unknown) { return { status: 200, data: d2 }; });
            }).catch(function(err: unknown) {
                const msg = err instanceof Error ? err.message : String(err || 'Unbekannter WebAuthn Fehler');
                if (isPatternError(err)) {
                    throw new Error('Passkey-Optionen wurden vom Browser abgelehnt (ungueltiges Credential-Format). Bitte Seite neu laden und erneut versuchen.');
                }
                throw err;
            });
        })
        .then(function(r2: ApiResponse<unknown>) {
            const data = r2.data as { success?: boolean };
            if (data && data.success) return true;
            throw new Error('Step-up fehlgeschlagen');
        });
    }

    function postFormWithStepupRetry(
        form: HTMLFormElement | null,
        url: string,
        buildFormData: () => FormData,
        options?: PostFormOptions
    ): Promise<ApiResponse<unknown>> {
        const onStepupRequired = options && typeof options.onStepupRequired === 'function' ? options.onStepupRequired : null;
        function send(triedCsrfRefresh: boolean, triedStepup: boolean): Promise<ApiResponse<unknown>> {
            const fd = buildFormData();
            return fetch(url, {
                method: 'POST',
                headers: { 'Accept': 'application/json' },
                body: fd
            })
            .then(function(res: Response) {
                return res.json().then(function(d: unknown) {
                    return { status: res.status, data: d };
                });
            })
            .then(function(r: ApiResponse<unknown>) {
                const data = (r && r.data) ? r.data as { code?: string; csrf_token?: string; error?: string; message?: string } : {};

                if (r && r.status === 400 && data && data.code === 'csrf' && data.csrf_token && !triedCsrfRefresh) {
                    setCsrfOnForm(form, data.csrf_token);
                    return send(true, triedStepup);
                }

                if (r && r.status === 403 && data && data.error === 'stepup_required' && !triedStepup) {
                    if (onStepupRequired) {
                        try {
                            onStepupRequired(data);
                        } catch (handlerErr) {
                            console.error('Step-up handler failed', handlerErr);
                        }
                    }
                    return doPasskeyStepup(getCsrfFromForm(form)).then(function() {
                        return send(triedCsrfRefresh, true);
                    });
                }

                return r;
            });
        }

        return send(false, false);
    }

    // Expose Step-up and helper functions globally for admin dashboard
    window.CybAuth = {
        b64urlToBuf: b64urlToBuf,
        bufToB64url: bufToB64url,
        b64ToBuf: b64ToBuf,
        doPasskeyStepup: doPasskeyStepup,
        postFormWithStepupRetry: postFormWithStepupRetry
    };


    function setupPasskeyLogin(): void {
        const btn = document.getElementById('passkeyLoginButton') as HTMLButtonElement | null;
        const form = document.getElementById('loginForm') as HTMLFormElement | null;
        const message = document.getElementById('loginMessage') as HTMLElement | null;
        if (!btn) return;

        if (!webauthnSupported()) {
            btn.style.display = 'none';
            return;
        }

        btn.addEventListener('click', function() {
            const csrf = getCsrfFromForm(form);
            btn.disabled = true;
            const orig = btn.textContent || '';
            btn.textContent = 'Warte auf Passkey...';

            window.api.post('/passkey-auth-options', { csrf_token: csrf })
            .then(function(data: unknown) {
                // Validate response with Zod schema
                const validation = PasskeyAuthenticationOptionsSchema.safeParse(data);
                if (!validation.success) {
                    console.warn('Passkey auth options validation failed:', validation.error);
                }
                const r: ApiResponse<PasskeyAuthResponse> = { status: 200, data: data as PasskeyAuthResponse };
                if (!r || !r.data || !r.data.publicKey || !r.data.challenge_id) {
                    if (r && r.data && r.data.error === 'webauthn_ip_origin_not_allowed') {
                        throw new Error(r.data.message || 'Passkeys require a domain name, not an IP address. Please use a hostname.');
                    }
                    throw new Error((r && r.data && (r.data.message || r.data.error)) || 'Passkey Login nicht verfuegbar');
                }
                const pk = r.data.publicKey as unknown as ExtendedPublicKeyCredentialRequestOptions;
                pk.challenge = b64urlToBuf(r.data.publicKey.challenge, 'auth.challenge');
                if (r.data.publicKey.allowCredentials && Array.isArray(r.data.publicKey.allowCredentials)) {
                    pk.allowCredentials = r.data.publicKey.allowCredentials.map(function(c, idx) {
                        return normalizeAllowCredential(c, 'auth.allowCredentials[' + idx + '].id');
                    });
                }
                return getCredentialWithFallback(pk).then(function(cred) {
                    if (!cred || !(cred as PublicKeyCredential).response) throw new Error('Abgebrochen');
                    const pkCred = cred as PublicKeyCredential;
                    const response = pkCred.response as AuthenticatorAssertionResponse;
                    return window.api.post('/passkey-auth-verify', {
                        csrf_token: csrf,
                        challenge_id: r.data.challenge_id,
                        id: pkCred.id,
                        clientDataJSON: bufToB64url(response.clientDataJSON),
                        authenticatorData: bufToB64url(response.authenticatorData),
                        signature: bufToB64url(response.signature),
                        userHandle: response.userHandle ? bufToB64url(response.userHandle) : ''
                    }).then(function(d2: unknown) { return { status: 200, data: d2 }; });
                });
            })
            .then(function(r2: ApiResponse<unknown>) {
                const data = r2.data as { success?: boolean; redirect?: string; error?: string; message?: string };
                if (data && data.success) {
                    window.location.href = (data.redirect || '/dashboard.php');
                    return;
                }
                throw new Error((data && (data.error || data.message)) || 'Anmeldung fehlgeschlagen');
            })
            .catch(function(e: unknown) {
                const msg = e instanceof Error ? e.message : 'Anmeldung fehlgeschlagen';
                showMessage(message, msg, 'error');
            })
            .finally(function() {
                btn.disabled = false;
                btn.textContent = orig;
            });
        });
    }

    function setupPasskeys(): void {
        const addBtn = document.getElementById('passkeyAddButton') as HTMLButtonElement | null;
        const listEl = document.getElementById('passkeyList') as HTMLElement | null;
        const message = document.getElementById('passkeyMessage') as HTMLElement | null;
        const form = document.getElementById('passkeyForm') as HTMLFormElement | null;
        if (!addBtn || !listEl) return;

        function csrf(): string {
            return getCsrfFromForm(form);
        }

        function render(items: PasskeyListItem[]): void {
            if (!listEl) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                listEl.innerHTML = 'Noch keine Passkeys hinterlegt.';
                return;
            }
            let html = '<div style="display:flex; flex-direction:column; gap:10px;">';
            all.forEach(function(it) {
                const label = it.label || 'Passkey';
                const created = it.created_at || '-';
                const lastUsed = it.last_used_at || '-';
                html += '<div style="display:flex; justify-content:space-between; gap:10px; align-items:center; padding:10px; background:#fff; border:1px solid #e2e8f0; border-radius:12px;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f;">' + escapeHtml(label) + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">created: ' + escapeHtml(created) + ' | last used: ' + escapeHtml(lastUsed) + '</div>'
                    + '</div>'
                    + '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-passkey-delete="' + escapeHtml(it.id) + '">Entfernen</button>'
                    + '</div>';
            });
            html += '</div>';
            listEl.innerHTML = html;
        }

        function load(): void {
            if (!listEl) return;
            listEl.innerHTML = 'Lade...';
            window.api.get('/passkeys-list')
                .then(function(data: unknown) {
                    // Validate response with Zod schema
                    const validation = PasskeyListResponseSchema.safeParse(data);
                    if (!validation.success) {
                        console.warn('Passkey list response validation failed:', validation.error);
                    }
                    const response = data as PasskeyListResponse;
                    if (response && response.success) render(response.items || []);
                    else render([]);
                })
                .catch(function() { render([]); });
        }

        function startRegister(label: string): Promise<boolean> {
            const c = csrf();
            return window.api.post('/passkey-register-options', { csrf_token: c })
            .then(function(data: unknown) {
                // Validate response with Zod schema
                const validation = PasskeyRegistrationOptionsSchema.safeParse(data);
                if (!validation.success) {
                    console.warn('Passkey registration options validation failed:', validation.error);
                }
                const r: ApiResponse<PasskeyRegisterResponse> & { csrf: string } = { status: 200, data: data as PasskeyRegisterResponse, csrf: c };
                if (!r || !r.data || !r.data.publicKey || !r.data.challenge_id) {
                    if (r && r.data && r.data.error === 'webauthn_ip_origin_not_allowed') {
                        throw new Error(r.data.message || 'Passkeys require a domain name, not an IP address. Please use a hostname.');
                    }
                    throw new Error((r && r.data && (r.data.message || r.data.error)) || 'Registrierung fehlgeschlagen');
                }
                const pk = r.data.publicKey as unknown as ExtendedPublicKeyCredentialCreationOptions;

                if (r.data.rp_id && pk.rp) {
                    pk.rp.id = r.data.rp_id;
                }

                if (!r.data.publicKey.challenge || typeof r.data.publicKey.challenge !== 'string') {
                    throw new Error('Ungueltige Challenge vom Server');
                }
                if (!r.data.publicKey.user || !r.data.publicKey.user.id || typeof r.data.publicKey.user.id !== 'string') {
                    throw new Error('Ungueltige User-ID vom Server');
                }

                try {
                    pk.challenge = b64ToBuf(r.data.publicKey.challenge, 'register.challenge');
                } catch (e) {
                    console.error('WebAuthn: challenge decode failed:', e instanceof Error ? e.message : e);
                    throw e;
                }

                try {
                    pk.user.id = b64ToBuf(r.data.publicKey.user.id, 'register.user.id');
                } catch (e) {
                    console.error('WebAuthn: user.id decode failed:', e instanceof Error ? e.message : e);
                    throw e;
                }

                if (r.data.publicKey.excludeCredentials) {
                    let excludeList = Array.isArray(r.data.publicKey.excludeCredentials) ? r.data.publicKey.excludeCredentials : [];
                    if (!Array.isArray(r.data.publicKey.excludeCredentials)) {
                        try {
                            const keys = Object.keys(r.data.publicKey.excludeCredentials).filter(function(k) { return !isNaN(parseInt(k)); }).sort(function(a,b){return parseInt(a)-parseInt(b);});
                            excludeList = keys.map(function(k) { return (r.data.publicKey!.excludeCredentials as unknown as Record<string, { type: string; id: string; transports?: string[] }>)[k]; });
                        } catch(e) {
                            excludeList = [];
                        }
                    }
                    pk.excludeCredentials = excludeList.map(function(c2, idx) {
                        if (!c2 || !c2.id) return null;
                        return { type: c2.type || 'public-key', id: b64ToBuf(c2.id, 'register.excludeCredentials[' + idx + '].id'), transports: c2.transports || [] };
                    }).filter(function(x): x is { type: string; id: ArrayBuffer; transports: string[] } { return x !== null; });
                    if (pk.excludeCredentials.length === 0) {
                        delete (pk as { excludeCredentials?: unknown }).excludeCredentials;
                    }
                }

                return navigator.credentials.create({ publicKey: pk as unknown as PublicKeyCredentialCreationOptions }).then(function(cred) {
                    if (!cred || !(cred as PublicKeyCredential).response) throw new Error('Abgebrochen');
                    const pkCred = cred as PublicKeyCredential;
                    const response = pkCred.response as AuthenticatorAttestationResponse;
                    let transports: string[] = [];
                    try {
                        if (response.getTransports) transports = response.getTransports();
                    } catch (e) {
                        transports = [];
                    }
                    return window.api.post('/passkey-register-verify', {
                        csrf_token: r.csrf,
                        challenge_id: r.data.challenge_id,
                        clientDataJSON: bufToB64url(response.clientDataJSON),
                        attestationObject: bufToB64url(response.attestationObject),
                        label: label || 'Passkey',
                        transports: transports
                    }).then(function(d2: unknown) { return { status: 200, data: d2 }; });
                }).catch(function(e: unknown) {
                    let errorMsg = 'Registrierung fehlgeschlagen';
                    console.error('WebAuthn create error:', e);
                    
                    if (e && typeof e === 'object' && 'name' in e) {
                        const errObj = e as { name: string; message?: string };
                        errorMsg = errObj.name + ': ' + (errObj.message || 'Unbekannter Fehler');
                        if (errObj.name === 'DOMException' || errObj.name === 'NotSupportedError' || errObj.name === 'InvalidStateError') {
                            errorMsg += '. Hinweis: Ihr Browser oder Betriebssystem unterstuetzt moeglicherweise keine Passkeys. ' +
                                       'Versuchen Sie einen aktuelleren Browser oder kontaktieren Sie den Support.';
                            console.error('Failed publicKey:', pk);
                        }
                    } else if (e && typeof e === 'object' && 'message' in e) {
                        errorMsg = (e as { message: string }).message;
                    }
                    throw new Error(errorMsg);
                });
            })
            .then(function(r2: ApiResponse<unknown>) {
                const data = r2.data as { success?: boolean; message?: string; error?: string };
                if (data && data.success) return true;
                throw new Error((data && (data.message || data.error)) || 'Registrierung fehlgeschlagen');
            });
        }

        addBtn.addEventListener('click', function() {
            if (!webauthnSupported()) {
                showMessage(message, 'Passkeys werden von diesem Browser nicht unterstuetzt.', 'error');
                return;
            }
            let label = '';
            try {
                label = window.prompt('Name fuer den Passkey (optional):', 'Mein Passkey') || '';
            } catch (e) {
                label = '';
            }
            addBtn.disabled = true;
            const orig = addBtn.textContent || '';
            addBtn.textContent = 'Warte auf Passkey...';
            startRegister(label)
                .then(function() {
                    showMessage(message, 'Passkey hinzugefuegt', 'success');
                    load();
                })
                .catch(function(e: unknown) {
                    const msg = e instanceof Error ? e.message : 'Registrierung fehlgeschlagen';
                    showMessage(message, msg, 'error');
                })
                .finally(function() {
                    addBtn.disabled = false;
                    addBtn.textContent = orig;
                });
        });

        listEl.addEventListener('click', function(e: Event) {
            const t = e.target as HTMLElement | null;
            if (!t || !t.getAttribute) return;
            const id = t.getAttribute('data-passkey-delete');
            if (!id) return;
            if (!window.confirm('Passkey wirklich entfernen?')) return;

            const c = csrf();
            showMessage(message, 'Bitte mit Passkey bestaetigen...', 'success');
            doPasskeyStepup(c)
                .then(function() {
                    return window.api.post('/passkey-delete', { csrf_token: c, id: id })
                        .then(function(d: unknown) { return { status: 200, data: d }; });
                })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r.data as { success?: boolean; message?: string };
                    if (data && data.success) {
                        showMessage(message, data.message || 'Entfernt', 'success');
                        load();
                        return;
                    }
                    throw new Error('Entfernen fehlgeschlagen');
                })
                .catch(function(e2: unknown) {
                    const msg = e2 instanceof Error ? e2.message : 'Entfernen fehlgeschlagen';
                    showMessage(message, msg, 'error');
                });
        });

        load();
    }


    function actionLabel(action: string, metaJson?: string): string {
        const a = String(action || '');
        if (a === 'admin_user_approve') return 'User freigeschaltet';
        if (a === 'admin_user_reject') return 'User abgelehnt';
        if (a === 'admin_user_delete') return 'User geloescht';
        if (a === 'admin_user_set_active') {
            try {
                const m = metaJson ? JSON.parse(metaJson) as { active?: number } : null;
                if (m && m.active === 1) return 'User aktiviert';
                if (m && m.active === 0) return 'User deaktiviert';
            } catch (e) {
                // ignore
            }
            return 'User Aktiv-Status geaendert';
        }
        if (a === 'admin_signup_settings_update') return 'Signup Einstellungen geaendert';
        if (a === 'admin_mail_settings_update') return 'SMTP Einstellungen geaendert';
        if (a === 'user_consent_privacy_notice') return 'DSGVO Hinweis akzeptiert';
        return a;
    }

    function setupAdminOverview(): void {
        const logsEl = document.getElementById('adminRecentLogs');
        const usersEl = document.getElementById('adminActiveUsers');
        if (!logsEl || !usersEl) return;

        function renderRecent(items: AuditLogItem[]): void {
            if (!logsEl) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                logsEl.innerHTML = 'Keine Eintraege.';
                return;
            }
            let html = '<div style="display:flex; flex-direction:column; gap:8px;">';
            all.forEach(function(r) {
                const label = actionLabel(r.action, r.meta_json);
                const actor = r.actor_email || '-';
                const target = r.target_email || '-';
                html += '<div style="display:flex; gap:10px; justify-content:space-between; align-items:flex-start; padding:8px; background:#fff; border:1px solid #e2e8f0; border-radius:12px;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f;">' + escapeHtml(label) + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">' + escapeHtml(r.created_at || '-') + ' | ' + escapeHtml(actor) + ' -> ' + escapeHtml(target) + '</div>'
                    + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096; white-space:nowrap;">' + escapeHtml(r.ip || '-') + '</div>'
                    + '</div>';
            });
            html += '</div>';
            logsEl.innerHTML = html;
        }

        function renderSessions(items: ActiveSession[]): void {
            if (!usersEl) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                usersEl.innerHTML = 'Niemand angemeldet.';
                return;
            }
            let html = '<div style="display:flex; flex-direction:column; gap:8px;">';
            all.forEach(function(u) {
                const status = String(u.account_status || '');
                html += '<div style="display:flex; gap:10px; justify-content:space-between; align-items:flex-start; padding:8px; background:#fff; border:1px solid #e2e8f0; border-radius:12px;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">' + escapeHtml(u.email || '-') + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">sessions: ' + escapeHtml(u.session_count || '0')
                    + ' | status: ' + escapeHtml(status)
                    + ' | ' + (parseInt(String(u.is_active) || '0', 10) === 1 ? 'aktiv' : 'inaktiv')
                    + '</div>'
                    + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096; white-space:nowrap;">expires: ' + escapeHtml(u.expires_at || '-') + '</div>'
                    + '</div>';
            });
            html += '</div>';
            usersEl.innerHTML = html;
        }

        function load(): void {
            window.api.get('/admin-audit-logs?limit=10')
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; items?: AuditLogItem[] };
                    if (response && response.success) {
                        renderRecent(response.items || []);
                    } else {
                        renderRecent([]);
                    }
                })
                .catch(function() { renderRecent([]); });

            window.api.get('/admin-active-sessions')
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; items?: ActiveSession[] };
                    if (response && response.success) {
                        renderSessions(response.items || []);
                    } else {
                        renderSessions([]);
                    }
                })
                .catch(function() { renderSessions([]); });
        }

        load();
        window.setInterval(load, 30000);
    }

    function setupLogs(): void {
        const list = document.getElementById('logsList');
        const message = document.getElementById('logsMessage');
        if (!list) return;

        const search = document.getElementById('logSearch') as HTMLInputElement | null;
        const kind = document.getElementById('logKind') as HTMLSelectElement | null;
        const refreshBtn = document.getElementById('logRefresh');

        function buildQuery(): string {
            const params: string[] = [];
            const q = search ? String(search.value || '').trim() : '';
            if (q) params.push('q=' + encodeURIComponent(q));
            return params.length ? ('?' + params.join('&')) : '';
        }

        function renderAudit(items: AuditLogItem[]): void {
            if (!list) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                list.innerHTML = 'Keine Eintraege.';
                return;
            }
            let html = '<div style="display:flex; flex-direction:column; gap:10px;">';
            all.forEach(function(r) {
                const label = actionLabel(r.action || '-', r.meta_json || '');
                html += '<details style="background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:10px;">'
                    + '<summary style="cursor:pointer; list-style:none;">'
                    + '<div style="display:flex; justify-content:space-between; gap:10px; align-items:center;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f;">' + escapeHtml(label) + '</div>'
                    + '<div style="font-size:0.8rem; color:#94a3b8;">' + escapeHtml(r.action || '') + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">' + escapeHtml(r.created_at || '-')
                    + ' | actor: ' + escapeHtml(r.actor_email || '-')
                    + ' | target: ' + escapeHtml(r.target_email || '-')
                    + '</div>'
                    + '</div>'
                    + '</div>'
                    + '</summary>'
                    + '<div style="margin-top:10px; font-size:0.92rem; color:#1a202c;">'
                    + '<div><strong>IP:</strong> ' + escapeHtml(r.ip || '-') + '</div>'
                    + '<div><strong>Remote:</strong> ' + escapeHtml(r.remote_addr || '-') + '</div>'
                    + '<div><strong>Forwarded-For:</strong> ' + escapeHtml(r.forwarded_for || '-') + '</div>'
                    + '<div><strong>User-Agent:</strong> ' + escapeHtml(r.user_agent || '-') + '</div>'
                    + '<div><strong>Meta:</strong> <pre style="white-space:pre-wrap; margin-top:6px; background:#f8fafc; border:1px solid #e2e8f0; padding:8px; border-radius:10px;">' + escapeHtml(r.meta_json || '') + '</pre></div>'
                    + '</div>'
                    + '</details>';
            });
            html += '</div>';
            list.innerHTML = html;
        }

        function renderConsent(items: ConsentLogItem[]): void {
            if (!list) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                list.innerHTML = 'Keine Eintraege.';
                return;
            }
            let html = '<div style="display:flex; flex-direction:column; gap:10px;">';
            all.forEach(function(r) {
                html += '<details style="background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:10px;">'
                    + '<summary style="cursor:pointer; list-style:none;">'
                    + '<div style="display:flex; justify-content:space-between; gap:10px; align-items:center;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f;">' + escapeHtml(r.email || '-') + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">' + escapeHtml(r.accepted_at || '-')
                    + ' | ' + escapeHtml(r.consent_key || '-')
                    + ' | v ' + escapeHtml(r.version || '-')
                    + '</div>'
                    + '</div>'
                    + '</div>'
                    + '</summary>'
                    + '<div style="margin-top:10px; font-size:0.92rem; color:#1a202c;">'
                    + '<div><strong>IP:</strong> ' + escapeHtml(r.ip || '-') + '</div>'
                    + '<div><strong>Remote:</strong> ' + escapeHtml(r.remote_addr || '-') + '</div>'
                    + '<div><strong>Forwarded-For:</strong> ' + escapeHtml(r.forwarded_for || '-') + '</div>'
                    + '<div><strong>User-Agent:</strong> ' + escapeHtml(r.user_agent || '-') + '</div>'
                    + '</div>'
                    + '</details>';
            });
            html += '</div>';
            list.innerHTML = html;
        }

        function load(): void {
            if (!list) return;
            list.innerHTML = 'Lade...';
            const k = kind ? String(kind.value || 'audit') : 'audit';
            const endpoint = (k === 'consent') ? '/admin-consent-logs' : '/admin-audit-logs';
            window.api.get(endpoint + buildQuery())
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; items?: AuditLogItem[] | ConsentLogItem[] };
                    if (!response || !response.success) {
                        list.innerHTML = 'Laden fehlgeschlagen.';
                        return;
                    }
                    if (k === 'consent') {
                        renderConsent((response.items || []) as ConsentLogItem[]);
                    } else {
                        renderAudit((response.items || []) as AuditLogItem[]);
                    }
                })
                .catch(function() {
                    if (list) list.innerHTML = 'Laden fehlgeschlagen.';
                });
        }

        if (refreshBtn) refreshBtn.addEventListener('click', load);
        if (kind) kind.addEventListener('change', load);
        if (search) {
            search.addEventListener('keydown', function(e: KeyboardEvent) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    load();
                }
            });
        }

        load();
    }


    function setupSignupSettings(): void {
        const form = document.getElementById('signupSettingsForm') as HTMLFormElement | null;
        const input = document.getElementById('signupTimeout') as HTMLInputElement | null;
        const intervalInput = document.getElementById('signupCleanupInterval') as HTMLInputElement | null;
        const message = document.getElementById('approvalsMessage');
        if (!form || !input || !intervalInput) return;

        function getCsrf(): string {
            if (!form) return '';
            const el = form.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
            return el ? el.value : '';
        }

        function load(): void {
            window.api.get('/admin-signup-settings')
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; timeout_minutes?: number; cleanup_interval_minutes?: number };
                    if (response && response.success) {
                        if (response.timeout_minutes && input) input.value = String(response.timeout_minutes);
                        if (response.cleanup_interval_minutes && intervalInput) intervalInput.value = String(response.cleanup_interval_minutes);
                    }
                })
                .catch(function() {
                    // ignore
                });
        }

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();
            if (!input || !intervalInput) return;
            const v = parseInt(input.value || '0', 10);
            const iv = parseInt(intervalInput.value || '0', 10);
            if (!v || v < 5 || v > 240 || !iv || iv < 5 || iv > 240) {
                showMessage(message, 'Ungueltiger Wert (5-240)', 'error');
                return;
            }

            const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            const orig = btn ? btn.textContent : '';
            if (btn) {
                btn.disabled = true;
                btn.textContent = 'Speichern...';
            }

            postFormWithStepupRetry(form!, '/api/v1/admin-signup-settings', function() {
                const fd = new FormData(form!);
                fd.set('csrf_token', getCsrf());
                fd.set('timeout_minutes', String(v));
                fd.set('cleanup_interval_minutes', String(iv));
                return fd;
            })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r && r.data ? r.data as { success?: boolean; message?: string; error?: string } : {};
                    if (data && data.success) {
                        showMessage(message, data.message || 'Gespeichert', 'success');
                        load();
                        if (window.CyberplexReloadApprovals) window.CyberplexReloadApprovals();
                        return;
                    }
                    showMessage(message, (data && (data.error || data.message)) ? String(data.error || data.message) : 'Speichern fehlgeschlagen', 'error');
                })
                .catch(function(err: unknown) {
                    const msg = err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
                    showMessage(message, msg, 'error');
                })
                .finally(function() {
                    if (btn) {
                        btn.disabled = false;
                        btn.textContent = orig || btn.textContent;
                    }
                });
        });

        load();
    }

    function setupSignup(): void {
        const toggle = document.getElementById('toggleSignup');
        const area = document.getElementById('signupArea');
        const form = document.getElementById('signupForm') as HTMLFormElement | null;
        const message = document.getElementById('signupMessage');

        function copyLoginEmailToSignupIfEmpty(): void {
            const loginEmail = document.getElementById('loginEmail') as HTMLInputElement | null;
            const signupEmail = document.getElementById('signupEmail') as HTMLInputElement | null;
            if (!signupEmail) return;
            const s = String(signupEmail.value || '').trim();
            if (s) return;
            const v = loginEmail ? String(loginEmail.value || '').trim() : '';
            if (v) signupEmail.value = v;
        }

        if (toggle && area) {
            toggle.addEventListener('click', function(e: Event) {
                e.preventDefault();
                area.classList.toggle('u-hidden');
                if (!area.classList.contains('u-hidden')) {
                    window.setTimeout(function() {
                        copyLoginEmailToSignupIfEmpty();
                        const first = document.getElementById('signupEmail');
                        if (first) first.focus();
                    }, 0);
                }
            });
        }

        if (!form) return;

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();
            if (form.getAttribute('data-submitting') === '1') return;
            form.setAttribute('data-submitting', '1');

            copyLoginEmailToSignupIfEmpty();

            const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            const originalText = submitBtn ? submitBtn.textContent : '';
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Senden...';
            }

            function send(attempt: number): Promise<void> {
                if (!form) return Promise.resolve();
                const formData = new FormData(form);
                return window.api.post('/signup', Object.fromEntries(formData))
                    .then(function(d: unknown) {
                        return { status: 200, data: d };
                    })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r && r.data ? r.data as { code?: string; csrf_token?: string; success?: boolean; error?: string } : {};
                    if (r && r.status === 400 && data && data.code === 'csrf' && data.csrf_token && attempt === 0) {
                        const csrfEl = form.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
                        if (csrfEl) csrfEl.value = String(data.csrf_token);
                        return send(1);
                    }

                    if (data && data.success) {
                        window.location.href = '/login.php?signup=verify';
                        return;
                    }
                    showMessage(message, (data && data.error) ? data.error : 'Registrierung fehlgeschlagen', 'error');
                });
            }

            send(0)
                .catch(function(error: unknown) {
                    showMessage(message, error instanceof Error ? error.message : 'Registrierung fehlgeschlagen', 'error');
                })
                .finally(function() {
                    form.setAttribute('data-submitting', '0');
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = originalText || submitBtn.textContent;
                    }
                });
        });
    }

    function setupLogin(): void {
        const form = document.getElementById('loginForm') as HTMLFormElement | null;
        const message = document.getElementById('loginMessage');
        const emailInput = document.getElementById('loginEmail') as HTMLInputElement | null;
        const reactivationStatus = document.getElementById('reactivationStatus');
        const reactivationBtn = document.getElementById('reactivationRequestBtn') as HTMLButtonElement | null;
        if (!form) return;

        try {
            const params = new URLSearchParams(window.location.search || '');
            if (params.get('signup') === 'verify') {
                showMessage(message, 'Bitte bestaetigen Sie Ihre E-Mail Adresse. Danach wird Ihr Konto vom Administrator geprueft.', 'success');
            } else if (params.get('signup') === 'pending') {
                showMessage(message, 'Registrierung eingegangen. Ihr Konto wird nach Pruefung freigeschaltet.', 'success');
            }
        } catch (e) {
            // ignore
        }

        if (emailInput && reactivationStatus) {
            let checkTimeout: number | null = null;
            emailInput.addEventListener('blur', function() {
                const email = String(emailInput.value || '').trim();
                if (!email || !email.includes('@')) return;
                
                if (checkTimeout) window.clearTimeout(checkTimeout);
                checkTimeout = window.setTimeout(function() {
                    window.api.get('/reactivation-status?email=' + encodeURIComponent(email))
                        .then(function(data: unknown) {
                            const response = data as ReactivationStatusResponse;
                            if (response && response.success && response.data && response.data.can_reactivate) {
                            const daysRemaining = response.data.days_remaining || 0;
                            const hasOpenTicket = response.data.has_open_ticket;
                            const ticket = response.data.ticket;
                            
                            const msgEl = reactivationStatus.querySelector('.cp-reactivation-status__message');
                            if (msgEl && window.I18n) {
                                msgEl.textContent = window.I18n.t('reactivation.status.message', { days: daysRemaining });
                            }
                            
                            const ticketEl = reactivationStatus.querySelector('.cp-reactivation-status__ticket');
                            if (ticketEl && hasOpenTicket && ticket) {
                                const ticketText = window.I18n ? 
                                    window.I18n.t('reactivation.status.ticket_open', { ticket: ticket.number }) + ' ' +
                                    window.I18n.t('reactivation.status.ticket_status', { status: ticket.status }) :
                                    'Es besteht bereits eine offene Reaktivierungsanfrage (' + ticket.number + '). Status: ' + ticket.status;
                                ticketEl.textContent = ticketText;
                                ticketEl.classList.remove('u-hidden');
                                if (reactivationBtn) reactivationBtn.classList.add('u-hidden');
                            } else if (ticketEl) {
                                ticketEl.classList.add('u-hidden');
                                if (reactivationBtn) reactivationBtn.classList.remove('u-hidden');
                            }
                            
                            reactivationStatus.classList.remove('u-hidden');
                        } else {
                            reactivationStatus.classList.add('u-hidden');
                        }
                    })
                    .catch(function() {
                        reactivationStatus.classList.add('u-hidden');
                    });
                }, 300);
            });
        }

        if (reactivationBtn) {
            reactivationBtn.addEventListener('click', function() {
                const email = emailInput ? String(emailInput.value || '').trim() : '';
                if (!email) return;

                const csrf = getCsrfFromForm(form);
                const origText = reactivationBtn.textContent;
                reactivationBtn.disabled = true;
                reactivationBtn.textContent = window.I18n ? window.I18n.t('reactivation.button.pending') : 'Anfrage laeuft...';

                window.api.post('/reactivation-ticket-request', {
                    csrf_token: csrf,
                    email: email
                })
                .then(function(data: unknown) {
                    showMessage(message, window.I18n ? window.I18n.t('reactivation.email.sent') : 'Falls ein deaktivierter Account existiert, wurde ein Link per E-Mail gesendet.', 'success');
                    if (reactivationStatus) reactivationStatus.classList.add('u-hidden');
                })
                .catch(function(err: unknown) {
                    showMessage(message, window.I18n ? window.I18n.t('reactivation.error.generic') : 'Fehler bei der Reaktivierung. Bitte versuchen Sie es spaeter erneut.', 'error');
                })
                .finally(function() {
                    reactivationBtn.disabled = false;
                    reactivationBtn.textContent = origText;
                });
            });
        }

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();

            function send(attempt: number): Promise<void> {
                if (!form) return Promise.resolve();
                const formData = new FormData(form);
                return window.api.post('/magic-link-request', Object.fromEntries(formData))
                    .then(function(data: unknown) {
                        // Validate response with Zod schema
                        const validation = LoginResponseSchema.safeParse(data);
                        if (!validation.success) {
                            console.warn('Magic link response validation failed:', validation.error);
                        }
                        return { status: 200, data: data };
                    })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r && r.data ? r.data as { code?: string; csrf_token?: string; retry_after_seconds?: number; error?: string; success?: boolean; message?: string } : {};

                    if (r && r.status === 400 && data && data.code === 'csrf' && data.csrf_token && attempt === 0) {
                        const csrfEl = form.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
                        if (csrfEl) csrfEl.value = String(data.csrf_token);
                        return send(1);
                    }

                    if (r.status === 429) {
                        const retry = data && data.retry_after_seconds ? parseInt(String(data.retry_after_seconds), 10) : 0;
                        let msg = (data && data.error === 'rate_limited') ? 'Bitte warten Sie kurz und versuchen Sie es erneut.' : (data.error || 'Bitte warten Sie kurz und versuchen Sie es erneut.');
                        if (retry && retry > 0) msg += ' (' + retry + 's)';
                        showMessage(message, msg, 'error');
                        return;
                    }

                    if (data && data.success) {
                        showMessage(message, data.message || 'Falls ein Konto existiert, wurde ein Link per E-Mail gesendet.', 'success');
                        return;
                    }
                    showMessage(message, (data && data.error) || 'Anfrage fehlgeschlagen', 'error');
                });
            }

            send(0)
                .catch(function() {
                    showMessage(message, 'Anfrage fehlgeschlagen', 'error');
                });
        });
    }


    function setupEmailChange(): void {
        const form = document.getElementById('emailChangeForm') as HTMLFormElement | null;
        const message = document.getElementById('emailChangeMessage');
        if (!form) return;

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();

            postFormWithStepupRetry(form!, '/api/v1/email-change', function() {
                return new FormData(form!);
            })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r && r.data ? r.data as { success?: boolean; message?: string; error?: string } : {};
                    if (data && data.success) {
                        showMessage(message, data.message || 'E-Mail geaendert', 'success');
                        return;
                    }
                    showMessage(message, (data && (data.error || data.message)) ? String(data.error || data.message) : 'E-Mail-Aenderung fehlgeschlagen', 'error');
                })
                .catch(function(err: unknown) {
                    const msg = err instanceof Error ? err.message : 'E-Mail-Aenderung fehlgeschlagen';
                    showMessage(message, msg, 'error');
                });
        });
    }

    function setupMailSettings(): void {
        const form = document.getElementById('mailSettingsForm') as HTMLFormElement | null;
        const message = document.getElementById('mailSettingsMessage');
        const testButton = document.getElementById('mailTestButton') as HTMLButtonElement | null
            || document.querySelector('button[onclick="testMailSettings()"]') as HTMLButtonElement | null;
        if (!form) return;

        // Validation helpers
        function isValidEmail(email: string): boolean {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        }

        function isValidPort(port: string | number): boolean {
            const portNum = typeof port === 'string' ? parseInt(port, 10) : port;
            return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
        }

        function validateForm(): { valid: boolean; errors: string[] } {
            const errors: string[] = [];

            const smtpHost = (form!.querySelector('#smtp_host') as HTMLInputElement | null)?.value || '';
            const smtpPort = (form!.querySelector('#smtp_port') as HTMLInputElement | null)?.value || '';
            const smtpUsername = (form!.querySelector('#smtp_username') as HTMLInputElement | null)?.value || '';
            const fromEmail = (form!.querySelector('#from_email') as HTMLInputElement | null)?.value || '';
            const toEmail = (form!.querySelector('#to_email') as HTMLInputElement | null)?.value || '';

            if (!smtpHost.trim()) {
                errors.push('SMTP Host ist erforderlich');
            }

            if (!isValidPort(smtpPort)) {
                errors.push('SMTP Port muss zwischen 1 und 65535 liegen');
            }

            if (!smtpUsername.trim()) {
                errors.push('SMTP Benutzername ist erforderlich');
            }

            if (!isValidEmail(fromEmail)) {
                errors.push('Absender E-Mail ist ungültig');
            }

            if (!isValidEmail(toEmail)) {
                errors.push('Admin E-Mail ist ungültig');
            }

            return { valid: errors.length === 0, errors };
        }

        function setButtonLoading(button: HTMLButtonElement | null, loading: boolean, originalText?: string): void {
            if (!button) return;
            if (loading) {
                button.disabled = true;
                button.setAttribute('data-original-text', button.textContent || '');
                button.textContent = 'Warte...';
            } else {
                button.disabled = false;
                const origText = button.getAttribute('data-original-text') || originalText;
                button.textContent = origText || button.textContent;
            }
        }

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();

            // Client-side validation
            const validation = validateForm();
            if (!validation.valid) {
                showMessage(message, validation.errors[0], 'error');
                return;
            }

            const submitBtn = form!.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            setButtonLoading(submitBtn, true);

            postFormWithStepupRetry(form!, '/api/v1/admin-mail-settings', function() {
                return new FormData(form!);
            })
                .then(function(r: ApiResponse<unknown>) {
                    const data = r && r.data ? r.data as { success?: boolean; message?: string; error?: string } : {};
                    if (data && data.success) {
                        showMessage(message, data.message || 'Gespeichert', 'success');
                        return;
                    }
                    showMessage(message, (data && (data.error || data.message)) ? String(data.error || data.message) : 'Speichern fehlgeschlagen', 'error');
                })
                .catch(function(err: unknown) {
                    const msg = err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
                    showMessage(message, msg, 'error');
                })
                .finally(function() {
                    setButtonLoading(submitBtn, false);
                });
        });

        if (testButton) {
            testButton.addEventListener('click', function() {
                // Client-side validation before test
                const validation = validateForm();
                if (!validation.valid) {
                    showMessage(message, validation.errors[0], 'error');
                    return;
                }

                setButtonLoading(testButton, true);

                postFormWithStepupRetry(form!, '/api/v1/admin-mail-test', function() {
                    return new FormData(form!);
                })
                    .then(function(r: ApiResponse<unknown>) {
                        const data = r && r.data ? r.data as { success?: boolean; message?: string; error?: string } : {};
                        if (data && data.success) {
                            showMessage(message, data.message || 'Testmail gesendet', 'success');
                            return;
                        }
                        showMessage(message, (data && (data.error || data.message)) ? String(data.error || data.message) : 'Test fehlgeschlagen', 'error');
                    })
                    .catch(function(err: unknown) {
                        const msg = err instanceof Error ? err.message : 'Test fehlgeschlagen';
                        showMessage(message, msg, 'error');
                    })
                    .finally(function() {
                        setButtonLoading(testButton, false);
                    });
            });
        }
    }

    function setupProfile(): void {
        const form = document.getElementById('profileForm') as HTMLFormElement | null;
        const message = document.getElementById('profileMessage');
        if (!form) return;

        const accountType = document.getElementById('accountType') as HTMLSelectElement | null;
        const businessFields = document.getElementById('businessFields');
        const privateFields = document.getElementById('privateFields');
        const shippingSame = document.getElementById('shippingSame') as HTMLInputElement | null;
        const shippingGroup = document.getElementById('shippingAddressGroup');

        function updateBusinessVisibility(): void {
            if (!accountType || !businessFields) return;
            if (accountType.value === 'business') {
                businessFields.classList.remove('u-hidden');
                if (privateFields) privateFields.classList.add('u-hidden');
            } else {
                businessFields.classList.add('u-hidden');
                if (privateFields) privateFields.classList.remove('u-hidden');
            }
        }

        function updateShippingVisibility(): void {
            if (!shippingSame || !shippingGroup) return;
            if (shippingSame.checked) {
                shippingGroup.classList.add('u-hidden');
            } else {
                shippingGroup.classList.remove('u-hidden');
            }
        }

        updateBusinessVisibility();
        updateShippingVisibility();

        if (accountType) {
            accountType.addEventListener('change', updateBusinessVisibility);
        }
        if (shippingSame) {
            shippingSame.addEventListener('change', updateShippingVisibility);
        }

        form.addEventListener('submit', function(e: Event) {
            e.preventDefault();
            if (form.getAttribute('data-submitting') === '1') return;
            form.setAttribute('data-submitting', '1');

            const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            const originalText = submitBtn ? submitBtn.textContent : '';
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Speichern...';
            }

            const formData = new FormData(form);

            // Use direct fetch for non-API endpoints (auth/ endpoints are not under /api/v1)
            fetch('/auth/profile-save.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': window.CSRF_TOKEN || ''
                },
                body: JSON.stringify(Object.fromEntries(formData)),
                credentials: 'same-origin'
            })
            .then(function(response) { return response.json(); })
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; message?: string; error?: string };
                    if (response.success) {
                        showMessage(message, response.message || 'Gespeichert', 'success');
                        return;
                    }
                    showMessage(message, response.error || 'Speichern fehlgeschlagen', 'error');
                })
                .catch(function() {
                    showMessage(message, 'Speichern fehlgeschlagen', 'error');
                })
            .finally(function() {
                form.setAttribute('data-submitting', '0');
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalText || submitBtn.textContent;
                }
            });
        });
    }

    function setupApprovals(): void {
        const container = document.getElementById('pendingUsers');
        const message = document.getElementById('approvalsMessage');
        if (!container) return;

        function render(items: PendingUser[]): void {
            if (!container) return;
            const all = Array.isArray(items) ? items : [];
            const pending = all.filter(function(u) { return (u && u.account_status === 'pending'); });
            const rejected = all.filter(function(u) { return (u && u.account_status === 'rejected'); });

            if (!pending.length && !rejected.length) {
                container.innerHTML = 'Keine offenen Anfragen.';
                return;
            }

            function row(u: PendingUser, actionsHtml: string): string {
                return '<div style="display:flex; gap:10px; align-items:center; justify-content:space-between; padding:10px; background:#fff; border:1px solid #e2e8f0; border-radius:12px;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:700; color:#1e3a5f; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">' + escapeHtml(u.email) + '</div>'
                    + '<div style="font-size:0.85rem; color:#718096;">Angelegt: ' + escapeHtml(u.created_at || '') + '</div>'
                    + '</div>'
                    + '<div style="display:flex; gap:8px;">'
                    + actionsHtml
                    + '</div>'
                    + '</div>';
            }

            let html = '';
            if (pending.length) {
                html += '<div style="font-weight:800; color:#1e3a5f; margin-bottom:8px;">Pending</div>';
                html += '<div style="display:flex; flex-direction:column; gap:10px; margin-bottom:14px;">';
                pending.forEach(function(u) {
                    html += row(u,
                        '<button type="button" class="cp-button cp-button--primary cp-button--sm" data-approve="' + escapeHtml(u.id) + '">Bestaetigen</button>'
                        + '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-reject="' + escapeHtml(u.id) + '">Ablehnen</button>'
                    );
                });
                html += '</div>';
            }

            if (rejected.length) {
                html += '<div style="font-weight:800; color:#1e3a5f; margin-bottom:8px;">Abgelehnt (loeschbar)</div>';
                html += '<div style="display:flex; flex-direction:column; gap:10px;">';
                rejected.forEach(function(u) {
                    html += row(u,
                        '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-delete="' + escapeHtml(u.id) + '">Loeschen</button>'
                    );
                });
                html += '</div>';
            }

            container.innerHTML = html;
        }

        function getCsrf(): string {
            const el = document.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
            return el ? el.value : '';
        }

        function load(): void {
            window.api.get('/admin-pending-users')
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; items?: PendingUser[] };
                    if (!response || !response.success) {
                        render([]);
                        return;
                    }
                    render(response.items || []);
                })
                .catch(function() {
                    if (container) container.innerHTML = 'Laden fehlgeschlagen.';
                });
        }

        function postAction(url: string, userId: string): void {
            function setCsrfTokenValue(v: string): void {
                const el = document.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
                if (el) el.value = String(v || '');
            }

            function send(triedStepup: boolean, triedCsrfRefresh: boolean): Promise<void> {
                const fd = new FormData();
                fd.append('csrf_token', getCsrf());
                fd.append('user_id', String(userId));

                const data: Record<string, string> = {};
                fd.forEach(function(value, key) {
                    data[key] = String(value);
                });

                return window.api.post(url.replace('/auth/', '/').replace('.php', ''), data)
                    .then(function(d: unknown) {
                        return { status: 200, data: d };
                    })
                    .then(function(r: ApiResponse<unknown>) {
                        const data = r && r.data ? r.data as { code?: string; csrf_token?: string; error?: string; message?: string; success?: boolean } : {};

                        if (r && r.status === 400 && data && data.code === 'csrf' && data.csrf_token && !triedCsrfRefresh) {
                            setCsrfTokenValue(data.csrf_token);
                            return send(triedStepup, true);
                        }

                        if (r && r.status === 403 && data && data.error === 'stepup_required' && !triedStepup) {
                            showMessage(message, data.message || 'Bitte mit Passkey bestaetigen...', 'success');
                            return doPasskeyStepup(getCsrf())
                                .then(function() {
                                    return send(true, triedCsrfRefresh);
                                })
                                .catch(function(e: unknown) {
                                    const msg = e instanceof Error ? e.message : 'Bitte mit Passkey bestaetigen...';
                                    showMessage(message, msg, 'error');
                                });
                        }

                        if (data && data.success) {
                            showMessage(message, data.message || 'OK', 'success');
                            load();
                            if (window.CyberplexReloadUsers) window.CyberplexReloadUsers();
                            return;
                        }
                        showMessage(message, (data && (data.message || data.error)) ? String(data.message || data.error) : 'Aktion fehlgeschlagen', 'error');
                    });
            }

            send(false, false).catch(function() {
                showMessage(message, 'Aktion fehlgeschlagen', 'error');
            });
        }

        container.addEventListener('click', function(e: Event) {
            const t = e.target as HTMLElement | null;
            if (!t) return;
            const approveId = t.getAttribute && t.getAttribute('data-approve');
            const rejectId = t.getAttribute && t.getAttribute('data-reject');
            const deleteId = t.getAttribute && t.getAttribute('data-delete');
            if (approveId) {
                postAction('/admin-user-approve', approveId);
            }
            if (rejectId) {
                postAction('/admin-user-reject', rejectId);
            }
            if (deleteId) {
                if (window.confirm('Abgelehnten Account wirklich loeschen? Danach kann die Adresse neu registriert werden.')) {
                    postAction('/admin-user-delete', deleteId);
                }
            }
        });

        window.CyberplexReloadApprovals = load;
        load();
    }


    function setupUserManagement(): void {
        const list = document.getElementById('usersList');
        const message = document.getElementById('usersMessage');
        if (!list) return;

        const search = document.getElementById('userSearch') as HTMLInputElement | null;
        const status = document.getElementById('userStatus') as HTMLSelectElement | null;
        const active = document.getElementById('userActive') as HTMLSelectElement | null;
        const refreshBtn = document.getElementById('userRefresh');

        function getCsrf(): string {
            const el = document.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
            return el ? el.value : '';
        }

        function buildQuery(): string {
            const params: string[] = [];
            const q = search ? String(search.value || '').trim() : '';
            const st = status ? String(status.value || 'all') : 'all';
            const ac = active ? String(active.value || 'all') : 'all';
            if (q) params.push('q=' + encodeURIComponent(q));
            if (st) params.push('status=' + encodeURIComponent(st));
            if (ac) params.push('active=' + encodeURIComponent(ac));
            return params.length ? ('?' + params.join('&')) : '';
        }

        function badge(text: string, bg: string, fg: string): string {
            return '<span style="display:inline-flex; align-items:center; padding:2px 8px; border-radius:999px; font-size:0.78rem; font-weight:700; background:'
                + bg + '; color:' + fg + ';">' + escapeHtml(text) + '</span>';
        }

        function render(items: UserListItem[]): void {
            if (!list) return;
            const all = Array.isArray(items) ? items : [];
            if (!all.length) {
                list.innerHTML = 'Keine Treffer.';
                return;
            }

            let html = '<div style="display:flex; flex-direction:column; gap:10px;">';

            all.forEach(function(u) {
                const statusText = u.account_status || '';
                const statusBadge = statusText === 'approved'
                    ? badge('approved', '#e6fffa', '#0f766e')
                    : (statusText === 'pending'
                        ? badge('pending', '#fffbeb', '#92400e')
                        : badge('rejected', '#fff5f5', '#b91c1c'));

                const activeBadge = (parseInt(String(u.is_active) || '0', 10) === 1)
                    ? badge('aktiv', '#f0fdf4', '#166534')
                    : badge('inaktiv', '#f1f5f9', '#334155');

                const adminBadge = (parseInt(String(u.is_admin) || '0', 10) === 1)
                    ? badge('admin', '#eef2ff', '#3730a3')
                    : '';

                const consentBadge = (parseInt(String(u.consent_ok) || '0', 10) === 1)
                    ? badge('DSGVO ok', '#f0fdf4', '#166534')
                    : badge('DSGVO fehlt', '#fff7ed', '#9a3412');

                let actions = '';
                if (statusText === 'pending') {
                    actions += '<button type="button" class="cp-button cp-button--primary cp-button--sm" data-approve="' + escapeHtml(u.id) + '">Bestaetigen</button>';
                    actions += '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-reject="' + escapeHtml(u.id) + '">Ablehnen</button>';
                } else if (statusText === 'rejected') {
                    actions += '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-delete="' + escapeHtml(u.id) + '">Loeschen</button>';
                } else if (statusText === 'approved' && parseInt(String(u.is_admin) || '0', 10) !== 1) {
                    if (parseInt(String(u.is_active) || '0', 10) === 1) {
                        actions += '<button type="button" class="cp-button cp-button--secondary cp-button--sm" data-active="0" data-user="' + escapeHtml(u.id) + '">Deaktivieren</button>';
                    } else {
                        actions += '<button type="button" class="cp-button cp-button--primary cp-button--sm" data-active="1" data-user="' + escapeHtml(u.id) + '">Aktivieren</button>';
                    }
                }

                let name = '';
                if (u.account_type === 'business') {
                    name = (u.company_name || '') ? String(u.company_name) : '';
                } else {
                    name = ((u.first_name || '') + ' ' + (u.last_name || '')).trim();
                }

                html += '<details style="background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:10px;">'
                    + '<summary style="cursor:pointer; list-style:none;">'
                    + '<div style="display:flex; align-items:center; justify-content:space-between; gap:10px;">'
                    + '<div style="min-width:0;">'
                    + '<div style="font-weight:800; color:#1e3a5f; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">' + escapeHtml(u.email) + '</div>'
                    + '<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:6px;">' + statusBadge + activeBadge + adminBadge + consentBadge + '</div>'
                    + '</div>'
                    + '<div style="display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end;">' + actions + '</div>'
                    + '</div>'
                    + '</summary>'
                    + '<div style="margin-top:10px; color:#1a202c; font-size:0.92rem;">'
                    + '<div><strong>Name/Firma:</strong> ' + escapeHtml(name || '-') + '</div>'
                    + '<div><strong>Typ:</strong> ' + escapeHtml(u.account_type || '-') + '</div>'
                    + '<div><strong>Telefon:</strong> ' + escapeHtml(u.phone || '-') + '</div>'
                    + '<div><strong>UID:</strong> ' + escapeHtml(u.uid_number || '-') + '</div>'
                    + '<div><strong>Ort:</strong> ' + escapeHtml(u.billing_city || '-') + ' ' + escapeHtml(u.billing_country || '') + '</div>'
                    + '<div><strong>Created:</strong> ' + escapeHtml(u.created_at || '-') + '</div>'
                    + '<div><strong>Last login:</strong> ' + escapeHtml(u.last_login || '-') + '</div>'
                    + '<div style="margin-top:10px;"><strong>DSGVO:</strong> ' + escapeHtml((parseInt(String(u.consent_ok) || '0', 10) === 1) ? 'akzeptiert' : 'nicht akzeptiert') + '</div>'
                    + '<div><strong>DSGVO accepted_at:</strong> ' + escapeHtml(u.consent_accepted_at || '-') + '</div>'
                    + '<div><strong>DSGVO IP:</strong> ' + escapeHtml(u.consent_ip || '-') + '</div>'
                    + '<div><strong>DSGVO Remote:</strong> ' + escapeHtml(u.consent_remote_addr || '-') + '</div>'
                    + '<div><strong>DSGVO User-Agent:</strong> ' + escapeHtml(u.consent_user_agent || '-') + '</div>'
                    + '</div>'
                    + '</details>';
            });

            html += '</div>';
            if (list) list.innerHTML = html;
        }

        function load(): void {
            if (!list) return;
            list.innerHTML = 'Lade...';
            window.api.get('/admin-users-list' + buildQuery())
                .then(function(data: unknown) {
                    const response = data as { success?: boolean; items?: UserListItem[] };
                    if (!response || !response.success) {
                        render([]);
                        return;
                    }
                    render(response.items || []);
                })
                .catch(function() {
                    if (list) list.innerHTML = 'Laden fehlgeschlagen.';
                });
        }

        function post(url: string, fields: Record<string, string>): void {
            function setCsrfTokenValue(v: string): void {
                const el = document.querySelector('input[name="csrf_token"]') as HTMLInputElement | null;
                if (el) el.value = String(v || '');
            }

            function send(triedStepup: boolean, triedCsrfRefresh: boolean): Promise<void> {
                const data: Record<string, string> = { csrf_token: getCsrf() };
                Object.keys(fields).forEach(function(k) {
                    data[k] = String(fields[k]);
                });

                return window.api.post(url.replace('/auth/', '/').replace('.php', ''), data)
                    .then(function(d: unknown) {
                        return { status: 200, data: d };
                    })
                    .then(function(r: ApiResponse<unknown>) {
                        const data = r && r.data ? r.data as { code?: string; csrf_token?: string; error?: string; message?: string; success?: boolean } : {};

                        if (r && r.status === 400 && data && data.code === 'csrf' && data.csrf_token && !triedCsrfRefresh) {
                            setCsrfTokenValue(data.csrf_token);
                            return send(triedStepup, true);
                        }

                        if (r && r.status === 403 && data && data.error === 'stepup_required' && !triedStepup) {
                            showMessage(message, data.message || 'Bitte mit Passkey bestaetigen...', 'success');
                            return doPasskeyStepup(getCsrf())
                                .then(function() {
                                    return send(true, triedCsrfRefresh);
                                })
                                .catch(function(e: unknown) {
                                    const msg = e instanceof Error ? e.message : 'Bitte mit Passkey bestaetigen...';
                                    showMessage(message, msg, 'error');
                                });
                        }

                        if (data && data.success) {
                            showMessage(message, data.message || 'OK', 'success');
                            load();
                            if (window.CyberplexReloadApprovals) window.CyberplexReloadApprovals();
                            return;
                        }
                        showMessage(message, (data && (data.message || data.error)) ? String(data.message || data.error) : 'Aktion fehlgeschlagen', 'error');
                    });
            }

            send(false, false).catch(function() {
                showMessage(message, 'Aktion fehlgeschlagen', 'error');
            });
        }

        list.addEventListener('click', function(e: Event) {
            const t = e.target as HTMLElement | null;
            if (!t || !t.getAttribute) return;
            const approveId = t.getAttribute('data-approve');
            const rejectId = t.getAttribute('data-reject');
            const deleteId = t.getAttribute('data-delete');
            const activeVal = t.getAttribute('data-active');
            const activeUser = t.getAttribute('data-user');

            if (approveId) {
                post('/admin-user-approve', { user_id: approveId });
            } else if (rejectId) {
                post('/admin-user-reject', { user_id: rejectId });
            } else if (deleteId) {
                if (window.confirm('Abgelehnten Account wirklich loeschen? Danach kann die Adresse neu registriert werden.')) {
                    post('/admin-user-delete', { user_id: deleteId });
                }
            } else if (activeUser && (activeVal === '0' || activeVal === '1')) {
                post('/admin-user-set-active', { user_id: activeUser, active: activeVal });
            }
        });

        if (refreshBtn) refreshBtn.addEventListener('click', load);
        if (status) status.addEventListener('change', load);
        if (active) active.addEventListener('change', load);
        if (search) {
            search.addEventListener('keydown', function(e: KeyboardEvent) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    load();
                }
            });
        }

        window.CyberplexReloadUsers = load;
        load();
    }

    function showMessage(el: HTMLElement | null, text: string, type: 'error' | 'success'): void {
        if (!el) return;
        el.textContent = text;
        el.classList.remove('error', 'success');
        el.classList.add(type);
        el.classList.remove('u-hidden');
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
